import { useMemo, useState } from 'react';
import { inTeam, settingsTeamFilter } from '../use-teams';
import {
  formatMoney,
  type RateAssignment,
  type RateAssignmentList,
  type RateCardTemplate,
  type RateUnit,
  type SaveRateAssignmentRequest,
  type SaveRateTemplateRequest,
  type WorkspaceCurrency,
} from '@forecastify/shared';
import { Button, Icon, Select, Tabs, TextInput } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { EditorDialog } from './editor-dialog';
import { DateField } from '../date-field';
import { Field } from '../card';
import { SettingsPage } from './settings-page';
import { SettingsTable, type SettingsRow } from './settings-table';
import { identityColor, initialsOf } from '../identity';
import { useAvatars } from '../use-avatar';
import { baseCurrencyOf } from '../base-currency';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Beállítások → Díjkártyák. C51, Phase 4.                                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Two tabs, as the mockup draws them: the cards a workspace curates, and who is on which.
 *
 * ⚠️⚠️ **COST AND TARGET ARE TWO COLUMNS AND ONE ROW.** The screen's own footnote: *"a költségdíj a
 * belső önköltség, a céldíj a kiszámlázott ár"*. Resourcing reads the first, billing the second,
 * and the margin is what sits between them.
 *
 * ⚠️⚠️ **THE ASSIGNMENT TAB IS ONE ROW PER PERSON — the mockup's own `wsRaRows`, ruled for by
 * name.** It shipped as every assignment, on the 15 Sep schedules argument; the answer was *"match
 * the mockup exactly"*. So the service hands back the rate in force and everybody with none, and
 * the two together are the workspace's people, once each. `rate-card.service.ts` records what the
 * shape costs: a future-dated assignment is not listed and not editable from here.
 */
export function RateCardSection(props: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly templates: readonly RateCardTemplate[];
  readonly assignments: RateAssignmentList;
  readonly currencies: readonly WorkspaceCurrency[];
  /** R2 · `active: false` for a deactivated person — offered for nothing new. */
  readonly people: readonly {
    readonly id: string;
    readonly name: string;
    readonly active?: boolean;
  }[];
  /** R-60 · the team filter, on the client: the workspace's teams and who is in which. */
  readonly teams?: readonly { readonly id: string; readonly name: string }[];
  readonly memberTeams?: ReadonlyMap<string, readonly string[]>;
  readonly canManage: boolean;
  readonly serverError: string | null;
  readonly onSaveTemplate: (id: string | null, body: SaveRateTemplateRequest) => Promise<boolean>;
  readonly onDeleteTemplate: (template: RateCardTemplate) => void;
  readonly onSaveAssignment: (
    id: string | null,
    body: SaveRateAssignmentRequest,
  ) => Promise<boolean>;
  readonly onDeleteAssignment: (assignment: RateAssignment) => void;
}) {
  const t = useT();
  /* 26 Oct 2026 · a save in flight, and whether the last one was refused — the dialog stays
     open until it lands, and shows its refusal inside. */
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [tab, setTab] = useState<'templates' | 'assignments'>('templates');
  const [query, setQuery] = useState('');
  const [unitFilter, setUnitFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [teamId, setTeamId] = useState('');

  const [editing, setEditing] = useState<TemplateDraft | null>(null);
  const [assigning, setAssigning] = useState<AssignmentDraft | null>(null);

  const avatars = useAvatars(
    props.assignments.assignments
      .map((one) => one.memberAvatarUrl)
      .concat(props.assignments.missing.map((one) => one.memberAvatarUrl)),
  );

  const active = props.currencies.filter((one) => one.active);
  /* 3 Nov 2026 · a new template or rate starts in the workspace's base (`base-currency.ts`). */
  const base = baseCurrencyOf(active)?.code ?? 'HUF';
  const symbolOf = (code: string | null): { code: string; symbol: string } =>
    props.currencies.find((one) => one.code === code) ?? { code: code ?? '', symbol: code ?? '' };

  const templates = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return props.templates.filter((one) => {
      /* ⚠️ The unit and the money are properties of the period in force TODAY, not of the
         card — so the filter asks the same question the column answers. */
      const now = currentPeriod(one);
      return (
        (needle === '' || one.name.toLowerCase().includes(needle)) &&
        (unitFilter === '' || now?.unit === unitFilter) &&
        (statusFilter === '' || (statusFilter === 'active') === one.active)
      );
    });
  }, [props.templates, query, unitFilter, statusFilter]);

  /**
   * ⚠️⚠️ **ONE ROW PER PERSON, WHICH IS THE MOCKUP'S OWN SHAPE.** The service returns the
   * assignment in force today and, beside it, everybody with no rate in force — so the two arrays
   * together are exactly the workspace's people, each once. The synthetic rows sort in with the
   * real ones by name: a block of *missing* at the bottom would be a second list to read, and the
   * reader scanning for somebody does not know in advance which half they are in.
   */
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const real = props.assignments.assignments.map((one) => ({ kind: 'row' as const, one }));
    const gaps = props.assignments.missing.map((one) => ({ kind: 'missing' as const, one }));
    return [...real, ...gaps]
      .filter(
        (entry) =>
          (needle === '' || entry.one.memberName.toLowerCase().includes(needle)) &&
          inTeam(props.memberTeams ?? new Map(), entry.one.memberId, teamId) &&
          /* ⚠️ A person with NO rate has no source, so any source filter hides them — which is
             right: *show me the custom rates* is not a question about who is missing one. */
          (sourceFilter === '' || (entry.kind === 'row' && entry.one.source === sourceFilter)),
      )
      .sort((a, b) => a.one.memberName.localeCompare(b.one.memberName));
  }, [props.assignments, props.memberTeams, query, sourceFilter, teamId]);

  /* ⚠️ A synthetic id for the people with no rate — `DataList` keys on it, and a member id
     alone could collide with an assignment id. The schedules screen does the same. */
  const rowIdOf = (entry: (typeof rows)[number]): string =>
    entry.kind === 'missing' ? `missing:${entry.one.memberId}` : entry.one.id;

  const templateSettingsRows: SettingsRow[] = templates.map((one) => {
    /* ⚠️ The row shows what applies TODAY — a card whose first period starts next year shows an
       em-dash rather than a price. */
    const now = currentPeriod(one);
    return {
      id: one.id,
      onOpen: !props.canManage
        ? undefined
        : () => {
            setEditing(draftOf(one));
          },
      cells: [
        { kind: 'mono', text: one.number, className: 'text-muted-foreground' },
        {
          kind: 'name',
          text: one.name,
          sub: t('settings.rates.sub', {
            periods: one.periods.length,
            people: one.assignedCount,
          }),
        },
        now === null
          ? { kind: 'num', text: '–', className: 'text-muted-foreground' }
          : { kind: 'num', text: formatMoney(Number(now.cost), symbolOf(now.currencyCode)) },
        now === null
          ? { kind: 'num', text: '–', className: 'text-muted-foreground' }
          : { kind: 'num', text: formatMoney(Number(now.target), symbolOf(now.currencyCode)) },
        now === null
          ? { kind: 'blank' }
          : { kind: 'text', text: unitLabel(t, now.unit), className: 'text-muted-foreground' },
        {
          kind: 'badge',
          text: one.active ? t('settings.currency.active') : t('settings.currency.inactive'),
          tone: one.active ? 'secondary' : 'outline',
          color: one.active ? 'var(--status-ok)' : 'var(--text-3)',
        },
        !props.canManage
          ? { kind: 'blank' }
          : {
              kind: 'kebab',
              ariaLabel: t('settings.rates.rowMenu', { name: one.name }),
              items: [
                {
                  label: t('common.edit'),
                  icon: 'pencil',
                  onSelect: () => {
                    setEditing(draftOf(one));
                  },
                },
                {
                  label: t('common.delete'),
                  icon: 'trash',
                  destructive: true,
                  onSelect: () => {
                    props.onDeleteTemplate(one);
                  },
                },
              ],
            },
      ],
    };
  });

  const assignmentSettingsRows: SettingsRow[] = rows.map((entry) => {
    const person = {
      kind: 'name' as const,
      text: entry.one.memberName,
      avatar: {
        initials: initialsOf(entry.one.memberName),
        ...(entry.one.memberAvatarSeed === null ? {} : { seed: entry.one.memberAvatarSeed }),
        color: identityColor(entry.one.memberId),
        src: avatars.get(entry.one.memberAvatarUrl ?? ''),
      },
    };

    /* ⚠️ A person with NO rate is a row, and it is the only one that offers *assign* rather than
     *edit* — *who is missing one* is what this tab is read for. */
    if (entry.kind === 'missing') {
      return {
        id: rowIdOf(entry),
        onOpen: !props.canManage
          ? undefined
          : () => {
              setAssigning({ ...emptyAssignment(base), memberId: entry.one.memberId });
            },
        cells: [
          person,
          { kind: 'text', text: t('settings.rates.noRate'), className: 'text-muted-foreground' },
          { kind: 'blank' },
          { kind: 'blank' },
          { kind: 'blank' },
          { kind: 'blank' },
          { kind: 'blank' },
          { kind: 'blank' },
        ],
      };
    }

    const one = entry.one;
    return {
      id: rowIdOf(entry),
      onOpen: !props.canManage
        ? undefined
        : () => {
            setAssigning(assignmentDraftOf(one, base));
          },
      cells: [
        person,
        { kind: 'text', text: one.templateName ?? t('settings.rates.customRate') },
        {
          kind: 'badge',
          text:
            one.source === 'template'
              ? t('settings.rates.fromTemplate')
              : t('settings.rates.fromCustom'),
          tone: one.source === 'template' ? 'secondary' : 'outline',
        },
        one.unit === null
          ? { kind: 'blank' }
          : { kind: 'text', text: unitLabel(t, one.unit), className: 'text-muted-foreground' },
        one.cost === null
          ? { kind: 'num', text: '–', className: 'text-muted-foreground' }
          : { kind: 'num', text: formatMoney(Number(one.cost), symbolOf(one.currencyCode)) },
        one.target === null
          ? { kind: 'num', text: '–', className: 'text-muted-foreground' }
          : { kind: 'num', text: formatMoney(Number(one.target), symbolOf(one.currencyCode)) },
        { kind: 'mono', text: one.fromDate, className: 'text-muted-foreground' },
        !props.canManage
          ? { kind: 'blank' }
          : {
              kind: 'kebab',
              ariaLabel: t('settings.rates.rowMenu', { name: one.memberName }),
              items: [
                {
                  label: t('common.edit'),
                  icon: 'pencil',
                  onSelect: () => {
                    setAssigning(assignmentDraftOf(one, base));
                  },
                },
                {
                  label: t('common.delete'),
                  icon: 'trash',
                  destructive: true,
                  onSelect: () => {
                    props.onDeleteAssignment(one);
                  },
                },
              ],
            },
      ],
    };
  });

  const onTemplates = tab === 'templates';

  return (
    <div data-screen="settings-rates" className="flex min-h-0 flex-1 flex-col">
      <SettingsPage
        title={t('settings.nav.rates')}
        action={
          !props.canManage
            ? undefined
            : {
                label: onTemplates ? t('settings.rates.new') : t('settings.rates.assign'),
                icon: 'plus',
                onSelect: () => {
                  if (onTemplates) setEditing(emptyTemplate(base));
                  else setAssigning(emptyAssignment(base));
                },
              }
        }
        tabs={
          <Tabs
            variant="underline"
            className="border-0"
            label={t('settings.nav.rates')}
            value={tab}
            onValueChange={(value) => {
              setTab(value === 'assignments' ? 'assignments' : 'templates');
            }}
            items={[
              {
                value: 'templates',
                label: t('settings.rates.tab.templates'),
                icon: 'credit-card',
                ...(props.loading === true ? {} : { count: props.templates.length }),
              },
              {
                value: 'assignments',
                label: t('settings.rates.tab.assignments'),
                icon: 'users',
                ...(props.loading === true ? {} : { count: props.assignments.assignments.length }),
              },
            ]}
          />
        }
        error={props.serverError}
        search={query}
        onSearch={setQuery}
        searchPlaceholder={
          onTemplates ? t('settings.rates.searchTemplates') : t('settings.rates.searchPeople')
        }
        filters={
          onTemplates
            ? [
                {
                  key: 'unit',
                  value: unitFilter,
                  ariaLabel: t('settings.rates.column.unit'),
                  options: [
                    { value: '', label: t('settings.rates.allUnits') },
                    { value: 'hour', label: unitLabel(t, 'hour') },
                    { value: 'day', label: unitLabel(t, 'day') },
                  ],
                  onChange: setUnitFilter,
                },
                {
                  key: 'status',
                  value: statusFilter,
                  ariaLabel: t('settings.rates.column.status'),
                  options: [
                    { value: '', label: t('settings.schedules.status.all') },
                    { value: 'active', label: t('settings.currency.active') },
                    { value: 'inactive', label: t('settings.currency.inactive') },
                  ],
                  onChange: setStatusFilter,
                },
              ]
            : [
                {
                  key: 'source',
                  value: sourceFilter,
                  ariaLabel: t('settings.rates.column.source'),
                  options: [
                    { value: '', label: t('settings.schedules.type.all') },
                    { value: 'template', label: t('settings.rates.fromTemplate') },
                    { value: 'custom', label: t('settings.rates.fromCustom') },
                  ],
                  onChange: setSourceFilter,
                },
                ...settingsTeamFilter(t, props.teams ?? [], teamId, setTeamId),
              ]
        }
      >
        {onTemplates ? (
          <SettingsTable
            grid="78px minmax(0,1fr) 118px 118px 96px 96px 36px"
            minWidth={880}
            columns={[
              { label: t('settings.rates.column.id') },
              { label: t('settings.rates.column.name') },
              { label: t('settings.rates.column.cost'), align: 'right' },
              { label: t('settings.rates.column.target'), align: 'right' },
              { label: t('settings.rates.column.unit') },
              { label: t('settings.rates.column.status') },
              {},
            ]}
            groups={[{ rows: templateSettingsRows }]}
            loading={props.loading}
            empty={{
              icon: 'credit-card',
              title: t('settings.rates.empty'),
              body: t('settings.rates.emptyBody'),
            }}
          />
        ) : (
          <SettingsTable
            grid="minmax(0,1fr) 150px 90px 92px 112px 112px 110px 36px"
            minWidth={1020}
            columns={[
              { label: t('settings.rates.column.person') },
              { label: t('settings.rates.column.card') },
              { label: t('settings.rates.column.source') },
              { label: t('settings.rates.column.unit') },
              { label: t('settings.rates.column.cost'), align: 'right' },
              { label: t('settings.rates.column.target'), align: 'right' },
              { label: t('settings.rates.column.from') },
              {},
            ]}
            groups={[{ rows: assignmentSettingsRows }]}
            loading={props.loading}
            empty={{
              icon: 'users',
              title: t('settings.rates.emptyAssignments'),
              body: t('settings.rates.emptyAssignmentsBody'),
            }}
          />
        )}
      </SettingsPage>

      <TemplateDialog
        draft={editing}
        currencies={active}
        onChange={setEditing}
        busy={saving}
        error={saveFailed ? props.serverError : null}
        onClose={() => {
          setSaveFailed(false);
          setEditing(null);
        }}
        onSave={(id, body) => {
          setSaving(true);
          void props.onSaveTemplate(id, body).then((landed) => {
            setSaving(false);
            setSaveFailed(!landed);
            if (landed) setEditing(null);
          });
        }}
      />

      <AssignmentDialog
        draft={assigning}
        templates={props.templates.filter((one) => one.active)}
        currencies={active}
        people={props.people}
        onChange={setAssigning}
        busy={saving}
        error={saveFailed ? props.serverError : null}
        onClose={() => {
          setSaveFailed(false);
          setAssigning(null);
        }}
        onSave={(id, body) => {
          setSaving(true);
          void props.onSaveAssignment(id, body).then((landed) => {
            setSaving(false);
            setSaveFailed(!landed);
            if (landed) setAssigning(null);
          });
        }}
      />
    </div>
  );
}

/* ── the drafts ────────────────────────────────────────────────────────────────────────────── */

interface PeriodDraft {
  fromDate: string;
  unit: RateUnit;
  currencyCode: string;
  cost: string;
  target: string;
}

interface TemplateDraft {
  id: string | null;
  name: string;
  active: boolean;
  periods: PeriodDraft[];
  version: number | undefined;
}

interface AssignmentDraft {
  id: string | null;
  memberId: string;
  fromDate: string;
  source: 'template' | 'custom';
  templateId: string;
  unit: RateUnit;
  currencyCode: string;
  cost: string;
  target: string;
  version: number | undefined;
}

const today = (): string => new Date().toISOString().slice(0, 10);

function emptyTemplate(currency: string): TemplateDraft {
  return {
    id: null,
    name: '',
    active: true,
    periods: [
      {
        fromDate: `${today().slice(0, 4)}-01-01`,
        unit: 'hour',
        currencyCode: currency,
        cost: '',
        target: '',
      },
    ],
    version: undefined,
  };
}

function draftOf(template: RateCardTemplate): TemplateDraft {
  return {
    id: template.id,
    name: template.name,
    active: template.active,
    periods: template.periods.map((one) => ({
      fromDate: one.fromDate,
      unit: one.unit,
      currencyCode: one.currencyCode,
      cost: one.cost,
      target: one.target,
    })),
    version: template.version,
  };
}

function emptyAssignment(base: string): AssignmentDraft {
  return {
    id: null,
    memberId: '',
    fromDate: today(),
    source: 'template',
    templateId: '',
    unit: 'hour',
    currencyCode: base,
    cost: '',
    target: '',
    version: undefined,
  };
}

function assignmentDraftOf(row: RateAssignment, base: string): AssignmentDraft {
  return {
    id: row.id,
    memberId: row.memberId,
    fromDate: row.fromDate,
    source: row.source,
    templateId: row.templateId ?? '',
    unit: row.unit ?? 'hour',
    currencyCode: row.currencyCode ?? base,
    cost: row.cost ?? '',
    target: row.target ?? '',
    version: row.version,
  };
}

/** ⚠️ One `t()` per branch with a LITERAL key — the i18n rule a computed key breaks. */
function unitLabel(t: Translate, unit: RateUnit): string {
  return unit === 'day' ? t('settings.rates.unit.day') : t('settings.rates.unit.hour');
}

/** What the card prices today. Null before its first period starts. */
function currentPeriod(template: RateCardTemplate): RateCardTemplate['periods'][number] | null {
  const now = today();
  let found: RateCardTemplate['periods'][number] | null = null;
  for (const period of template.periods) {
    if (period.fromDate <= now && (found === null || period.fromDate > found.fromDate)) {
      found = period;
    }
  }
  return found;
}

const numberOf = (value: string): number => Number(value.replace(/\s/g, '').replace(',', '.'));

function TemplateDialog({
  draft,
  currencies,
  onChange,
  onClose,
  onSave,
  busy,
  error,
}: {
  readonly draft: TemplateDraft | null;
  readonly currencies: readonly WorkspaceCurrency[];
  readonly onChange: (draft: TemplateDraft) => void;
  readonly onClose: () => void;
  readonly onSave: (id: string | null, body: SaveRateTemplateRequest) => void;
  /* 26 Oct 2026 · the save in flight, and its refusal — shown here, the dialog stays open. */
  readonly busy: boolean;
  readonly error: string | null;
}) {
  const t = useT();
  if (draft === null) return null;

  const usable =
    draft.name.trim() !== '' &&
    draft.periods.length > 0 &&
    draft.periods.every(
      (one) =>
        one.fromDate !== '' &&
        Number.isFinite(numberOf(one.cost)) &&
        Number.isFinite(numberOf(one.target)),
    );

  return (
    <EditorDialog
      open
      width="min(720px, 96vw)"
      title={draft.id === null ? t('settings.rates.dialog.new') : t('settings.rates.dialog.edit')}
      saveDisabled={!usable}
      onClose={onClose}
      busy={busy}
      error={error}
      onSave={() => {
        onSave(draft.id, {
          name: draft.name.trim(),
          active: draft.active,
          periods: draft.periods.map((one) => ({
            fromDate: one.fromDate,
            unit: one.unit,
            currencyCode: one.currencyCode,
            cost: numberOf(one.cost),
            target: numberOf(one.target),
          })),
          ...(draft.version === undefined ? {} : { version: draft.version }),
        });
      }}
    >
      <Field label={t('settings.rates.column.name')} required>
        <TextInput
          value={draft.name}
          placeholder={t('settings.rates.namePlaceholder')}
          onChange={(event) => {
            onChange({ ...draft, name: event.target.value });
          }}
        />
      </Field>

      {/**
       * ⚠️⚠️ **PERIODS ARE EDITED AS A SET, WHICH IS WHY THE SAVE SENDS ALL OF THEM.** Somebody
       * adds next year's rate beside this year's and saves once; a diff would need ids the form
       * does not have for the new row.
       */}
      <div className="flex flex-col gap-2">
        <div className="text-muted-foreground flex items-center text-[11px] font-medium tracking-wide uppercase">
          <span className="flex-1">{t('settings.rates.periods')}</span>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-[11.5px]"
            onClick={() => {
              const last = draft.periods[draft.periods.length - 1];
              onChange({
                ...draft,
                periods: [
                  ...draft.periods,
                  {
                    fromDate: '',
                    unit: last?.unit ?? 'hour',
                    currencyCode: last?.currencyCode ?? baseCurrencyOf(currencies)?.code ?? 'HUF',
                    cost: '',
                    target: '',
                  },
                ],
              });
            }}
          >
            <Icon name="plus" size={12} />
            {t('settings.rates.addPeriod')}
          </Button>
        </div>

        {draft.periods.map((period, index) => (
          <div
            key={index}
            className="grid items-end gap-2 rounded-lg border p-2.5 sm:grid-cols-[140px_110px_100px_1fr_1fr_28px]"
          >
            <Field label={t('settings.rates.column.from')} required>
              <DateField
                label={t('settings.rates.column.from')}
                value={period.fromDate}
                clearable={false}
                onChange={(next) => {
                  onChange({
                    ...draft,
                    periods: draft.periods.map((one, at) =>
                      at === index ? { ...one, fromDate: next } : one,
                    ),
                  });
                }}
              />
            </Field>
            <Field label={t('settings.rates.column.unit')}>
              <Select
                value={period.unit}
                onChange={(event) => {
                  const unit = event.target.value === 'day' ? 'day' : 'hour';
                  onChange({
                    ...draft,
                    periods: draft.periods.map((one, at) =>
                      at === index ? { ...one, unit } : one,
                    ),
                  });
                }}
                options={[
                  { value: 'hour', label: t('settings.rates.unit.hour') },
                  { value: 'day', label: t('settings.rates.unit.day') },
                ]}
              />
            </Field>
            <Field label={t('settings.rates.column.currency')}>
              <Select
                value={period.currencyCode}
                onChange={(event) => {
                  onChange({
                    ...draft,
                    periods: draft.periods.map((one, at) =>
                      at === index ? { ...one, currencyCode: event.target.value } : one,
                    ),
                  });
                }}
                options={currencies.map((one) => ({ value: one.code, label: one.code }))}
              />
            </Field>
            <Field label={t('settings.rates.column.cost')} required>
              <TextInput
                value={period.cost}
                className="tabular-nums"
                onChange={(event) => {
                  onChange({
                    ...draft,
                    periods: draft.periods.map((one, at) =>
                      at === index ? { ...one, cost: event.target.value } : one,
                    ),
                  });
                }}
              />
            </Field>
            <Field label={t('settings.rates.column.target')} required>
              <TextInput
                value={period.target}
                className="tabular-nums"
                onChange={(event) => {
                  onChange({
                    ...draft,
                    periods: draft.periods.map((one, at) =>
                      at === index ? { ...one, target: event.target.value } : one,
                    ),
                  });
                }}
              />
            </Field>
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground mb-1"
              aria-label={t('settings.rates.removePeriod')}
              /* ⚠️ The last period cannot be removed: a card with none prices nothing, and the
                 server refuses it anyway. Disabled says so before the refusal does. */
              disabled={draft.periods.length === 1}
              onClick={() => {
                onChange({
                  ...draft,
                  periods: draft.periods.filter((_, at) => at !== index),
                });
              }}
            >
              <Icon name="trash" size={13} />
            </Button>
          </div>
        ))}
      </div>

      <label className="flex items-center gap-2 text-[12.5px]">
        <input
          type="checkbox"
          className="accent-primary size-4"
          checked={draft.active}
          onChange={(event) => {
            onChange({ ...draft, active: event.target.checked });
          }}
        />
        {t('settings.rates.activeLabel')}
      </label>
    </EditorDialog>
  );
}

function AssignmentDialog({
  draft,
  templates,
  currencies,
  people,
  onChange,
  onClose,
  onSave,
  busy,
  error,
}: {
  readonly draft: AssignmentDraft | null;
  readonly templates: readonly RateCardTemplate[];
  readonly currencies: readonly WorkspaceCurrency[];
  /** R2 · `active: false` for a deactivated person — offered for nothing new. */
  readonly people: readonly {
    readonly id: string;
    readonly name: string;
    readonly active?: boolean;
  }[];
  readonly onChange: (draft: AssignmentDraft) => void;
  readonly onClose: () => void;
  readonly onSave: (id: string | null, body: SaveRateAssignmentRequest) => void;
  readonly busy: boolean;
  readonly error: string | null;
}) {
  const t = useT();
  if (draft === null) return null;

  const custom = draft.source === 'custom';
  const usable =
    draft.memberId !== '' &&
    draft.fromDate !== '' &&
    (custom
      ? Number.isFinite(numberOf(draft.cost)) && Number.isFinite(numberOf(draft.target))
      : draft.templateId !== '');

  return (
    <EditorDialog
      open
      title={
        draft.id === null ? t('settings.rates.dialog.assign') : t('settings.rates.dialog.reassign')
      }
      saveDisabled={!usable}
      onClose={onClose}
      busy={busy}
      error={error}
      onSave={() => {
        onSave(
          draft.id,
          custom
            ? {
                source: 'custom',
                memberId: draft.memberId,
                fromDate: draft.fromDate,
                unit: draft.unit,
                currencyCode: draft.currencyCode,
                cost: numberOf(draft.cost),
                target: numberOf(draft.target),
                ...(draft.version === undefined ? {} : { version: draft.version }),
              }
            : {
                source: 'template',
                memberId: draft.memberId,
                fromDate: draft.fromDate,
                templateId: draft.templateId,
                ...(draft.version === undefined ? {} : { version: draft.version }),
              },
        );
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('settings.rates.column.person')} required>
          <Select
            value={draft.memberId}
            onChange={(event) => {
              onChange({ ...draft, memberId: event.target.value });
            }}
            options={[
              { value: '', label: t('settings.rates.pickPerson') },
              /* R2 · no deactivated person, except the one this assignment already names. */
              ...people
                .filter((one) => one.active !== false || one.id === draft.memberId)
                .map((one) => ({ value: one.id, label: one.name })),
            ]}
          />
        </Field>
        <Field label={t('settings.rates.column.from')} required>
          <DateField
            label={t('settings.rates.column.from')}
            value={draft.fromDate}
            clearable={false}
            onChange={(next) => {
              onChange({ ...draft, fromDate: next });
            }}
          />
        </Field>
      </div>

      {/**
       * ⚠️ **Two arms, and the form shows one at a time.** The table's own constraint refuses a row
       * carrying both, so offering both sets of fields at once would invite a shape the server
       * cannot store.
       */}
      <Field label={t('settings.rates.column.source')}>
        <Select
          value={draft.source}
          onChange={(event) => {
            onChange({ ...draft, source: event.target.value === 'custom' ? 'custom' : 'template' });
          }}
          options={[
            { value: 'template', label: t('settings.rates.fromTemplate') },
            { value: 'custom', label: t('settings.rates.fromCustom') },
          ]}
        />
      </Field>

      {custom ? (
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label={t('settings.rates.column.unit')}>
            <Select
              value={draft.unit}
              onChange={(event) => {
                onChange({ ...draft, unit: event.target.value === 'day' ? 'day' : 'hour' });
              }}
              options={[
                { value: 'hour', label: t('settings.rates.unit.hour') },
                { value: 'day', label: t('settings.rates.unit.day') },
              ]}
            />
          </Field>
          <Field label={t('settings.rates.column.currency')}>
            <Select
              value={draft.currencyCode}
              onChange={(event) => {
                onChange({ ...draft, currencyCode: event.target.value });
              }}
              options={currencies.map((one) => ({ value: one.code, label: one.code }))}
            />
          </Field>
          <Field label={t('settings.rates.column.cost')} required>
            <TextInput
              value={draft.cost}
              className="tabular-nums"
              onChange={(event) => {
                onChange({ ...draft, cost: event.target.value });
              }}
            />
          </Field>
          <Field label={t('settings.rates.column.target')} required>
            <TextInput
              value={draft.target}
              className="tabular-nums"
              onChange={(event) => {
                onChange({ ...draft, target: event.target.value });
              }}
            />
          </Field>
        </div>
      ) : (
        <Field label={t('settings.rates.column.card')} required>
          <Select
            value={draft.templateId}
            onChange={(event) => {
              onChange({ ...draft, templateId: event.target.value });
            }}
            options={[
              { value: '', label: t('settings.rates.pickTemplate') },
              ...templates.map((one) => ({ value: one.id, label: one.name })),
            ]}
          />
        </Field>
      )}
    </EditorDialog>
  );
}
